// ** Modules
import React from 'react';
import { signIn } from 'next-auth/react';

// ** UI
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Fade from '@mui/material/Fade';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

// ** Components
import Section from '@/components/common/Section';

// ** Icons
import Code from '@mui/icons-material/Code';
import Check from '@mui/icons-material/Check';
import Schema from '@mui/icons-material/Schema';
import Refresh from '@mui/icons-material/Refresh';
import Terminal from '@mui/icons-material/Terminal';
import BugReport from '@mui/icons-material/BugReport';
import PersonIcon from '@mui/icons-material/Person';
import ContentCopy from '@mui/icons-material/ContentCopy';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ErrorOutlined from '@mui/icons-material/ErrorOutlined';

// ** Utils
import { parseStrapiValidationErrors } from '@/utils/shared/errorParser';

// ** Context
import { useHeaderOverride } from '@/context/HeaderOverrideContext';

// ** Style
import { DIN } from '@/styles/globalStyles';

// ** Types
import type { Provider } from 'next-auth/providers';

type DevErrorProps = {
	message?: string;
	error?: {
		message: string;
		stack?: string;
	};
	slug?: string;
	providers?: Provider;
};

const DevErrorMode = ({ message, error, slug, providers }: DevErrorProps) => {
	const { setOverride } = useHeaderOverride();
	const [tab, setTab] = React.useState(0);
	const [copied, setCopied] = React.useState(false);
	const isDev = process.env.NODE_ENV === 'development' && !!error;

	const validationErrors = error?.message ? parseStrapiValidationErrors(error.message) : [];
	const isValidationError =
		validationErrors.length > 0 ||
		!!error?.message?.includes('cannot represent value') ||
		!!error?.message?.includes('VALIDATION');

	React.useEffect(() => {
		setOverride({ appBarVariant: 'light', showMenu: true });
		return () => setOverride(null);
	}, [setOverride]);

	const handleCopy = () => {
		if (error?.stack) {
			navigator.clipboard.writeText(error.stack);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		}
	};

	const handleSignIn = () => {
		let providerId: string | null = null;
		if (Array.isArray(providers)) {
			providerId = providers[0]?.id || null;
		} else if (typeof providers === 'object' && providers !== null) {
			const providerKeys = Object.keys(providers);
			if (providerKeys.length > 0) providerId = providerKeys[0];
		}

		if (providerId) {
			signIn(providerId);
		} else {
			signIn();
		}
	};

	return (
		<Section containerSx={{ mt: 30 }}>
			<Fade in timeout={600}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						pt: 12,
						pb: 10,
						gap: 4
					}}
				>
					{/* 1. HERO SECTION: The Big Catch */}
					<Stack spacing={2} sx={{ textAlign: 'center', alignItems: 'center' }}>
						<Box
							sx={{
								p: 3,
								borderRadius: '50%',
								bgcolor: isValidationError ? 'warning.light' : 'error.light',
								color: isValidationError ? 'warning.main' : 'error.main',
								display: 'flex',
								mb: 1
							}}
						>
							<BugReport sx={{ fontSize: 80 }} />
						</Box>

						<Typography variant="display-md" sx={{ fontWeight: 800 }}>
							{isDev ? (isValidationError ? 'Schema Mismatch' : 'Entwicklungs-Fehler') : 'System-Wartung'}
						</Typography>

						<Typography variant="body-lg" color="text.secondary" sx={{ maxWidth: 600 }}>
							{isDev
								? `Auf der Seite /${slug || ''} ist ein technisches Problem aufgetreten.`
								: message || 'Wir sind gleich wieder da.'}
						</Typography>
					</Stack>

					{/* 2. TECHNICAL CONTENT AREA */}
					{isDev && (
						<Paper
							elevation={0}
							sx={{
								width: '100%',
								border: '1px solid',
								borderColor: 'divider',
								borderRadius: 4,
								overflow: 'hidden',
								bgcolor: 'background.paper'
							}}
						>
							{/* Sub-Header with Context */}
							<Box
								sx={{
									px: 3,
									py: 2,
									bgcolor: 'grey.50',
									borderBottom: '1px solid',
									borderColor: 'divider'
								}}
							>
								<Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
									<Terminal sx={{ fontSize: 18, color: 'text.disabled' }} />
									<Typography variant="label" sx={{ color: 'text.secondary', fontWeight: 700 }}>
										DEBUGGER <ChevronRight sx={{ fontSize: 14, verticalAlign: 'middle' }} />{' '}
										{isValidationError ? 'VALIDATION_REPORT' : 'RUNTIME_EXCEPTION'}
									</Typography>
								</Stack>
							</Box>

							<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
								<Tab
									icon={<Schema fontSize="small" />}
									iconPosition="start"
									label="Visual Report"
									sx={{ minHeight: 50 }}
								/>
								<Tab icon={<Code fontSize="small" />} iconPosition="start" label="Stack Trace" sx={{ minHeight: 50 }} />
							</Tabs>

							<Box
								sx={{
									p: 3,
									minHeight: 300,
									bgcolor: tab === 1 ? '#0d1117' : 'transparent'
								}}
							>
								{tab === 0 ? (
									<Stack spacing={2}>
										{/* Render explicitly parsed structural errors if available */}
										{validationErrors.length > 0 ? (
											validationErrors.map((err) => {
												const errorKey = `${err.component}-${err.field}-${err.id}`;
												return (
													<Box
														key={errorKey}
														sx={{
															p: 2.5,
															borderRadius: 2,
															bgcolor: 'grey.50',
															border: '1px solid',
															borderColor: 'grey.200'
														}}
													>
														<Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
															<ErrorOutlined color="error" sx={{ mt: 0.3 }} />
															<Box>
																<Typography variant="title-sm" sx={{ display: 'block', mb: 0.5 }}>
																	{err.component}
																</Typography>
																<Typography variant="body-sm" sx={{ color: 'error.main' }}>
																	field: <strong>{err.field}</strong> is null but required
																</Typography>
															</Box>
														</Stack>
													</Box>
												);
											})
										) : isValidationError ? (
											/* Render a clean container for Enum and Schema Mismatches */
											<Box
												sx={{
													p: 2.5,
													borderRadius: 2,
													bgcolor: 'warning.light',
													border: '1px solid',
													borderColor: 'warning.main'
												}}
											>
												<Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
													<Schema color="warning" />
													<Box>
														<Typography
															variant="title-sm"
															sx={{
																display: 'block',
																mb: 0.5,
																color: 'warning.dark',
																fontWeight: 700
															}}
														>
															GraphQL Schema Mismatch (Enum Evaluation Failed)
														</Typography>
														<Typography
															variant="body-sm"
															sx={{
																color: 'text.primary',
																fontFamily: 'monospace'
															}}
														>
															{error?.message}
														</Typography>
													</Box>
												</Stack>
											</Box>
										) : (
											/* Standard fallback Runtime Exceptions */
											<Box
												sx={{
													p: 2,
													bgcolor: 'error.main',
													color: 'common.white',
													borderRadius: 2
												}}
											>
												<Typography variant="label" sx={{ display: 'block', mb: 1, opacity: 0.8 }}>
													RAW GRAPHQL ERROR
												</Typography>
												<Typography
													variant="body-md"
													sx={{
														fontFamily: DIN
													}}
												>
													{error?.message}
												</Typography>
											</Box>
										)}
									</Stack>
								) : (
									<Box
										sx={{
											position: 'relative',
											borderRadius: 2,
											overflow: 'hidden'
										}}
									>
										{/* Code Block Header */}
										<Box
											sx={{
												display: 'flex',
												justifyContent: 'flex-end',
												alignItems: 'center',
												px: 2,
												py: 1,
												bgcolor: '#161b22',
												borderBottom: '1px solid #30363d'
											}}
										>
											<Button
												size="small"
												onClick={handleCopy}
												startIcon={copied ? <Check fontSize="inherit" /> : <ContentCopy fontSize="inherit" />}
												sx={{
													color: copied ? 'success.main' : 'common.white',
													textTransform: 'none',
													fontSize: '0.7rem',
													transition: 'all 0.2s ease-in-out',
													'&:hover': {
														bgcolor: 'rgba(255,255,255,0.05)',
														color: copied ? 'success.light' : 'common.white'
													}
												}}
											>
												{copied ? 'Copied!' : 'Copy Stack'}
											</Button>
										</Box>

										{/* The Code Content */}
										<Typography
											component="pre"
											sx={{
												color: '#c9d1d9',
												fontFamily: DIN,
												fontSize: '0.75rem',
												overflowX: 'auto',
												p: 2,
												m: 0,
												lineHeight: 1.6,
												maxHeight: '400px',
												bgcolor: '#0d1117'
											}}
										>
											{error?.stack}
										</Typography>
									</Box>
								)}
							</Box>
						</Paper>
					)}

					{/* 3. ACTIONS */}
					<Stack direction="row" spacing={2}>
						<Button
							variant="contained"
							size="large"
							startIcon={<Refresh />}
							onClick={() => window.location.reload()}
							sx={{ borderRadius: 10, px: 4 }}
						>
							Erneut versuchen
						</Button>

						{isDev && (
							<Button
								variant="outlined"
								size="large"
								startIcon={<PersonIcon />}
								onClick={handleSignIn}
								sx={{ borderRadius: 10, px: 4 }}
							>
								Account wechseln
							</Button>
						)}
					</Stack>
				</Box>
			</Fade>
		</Section>
	);
};

export default DevErrorMode;
